import { StyleSheet } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';

export const getCompetitionsHubStyles = (theme: Theme) =>
  StyleSheet.create({
    screen: { flex: 1, backgroundColor: theme.background.primary },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingHorizontal: 16,
      paddingTop: 14,
      paddingBottom: 4,
    },
    headerTitle: {
      fontSize: theme.typography.h2.fontSize,
      fontWeight: theme.typography.h2.fontWeight,
      color: theme.black,
    },
    scrollContent: { paddingHorizontal: 16, paddingBottom: 48 },
    sectionHeader: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.gray[500],
      letterSpacing: 1,
      textTransform: 'uppercase',
      marginTop: 18,
      marginBottom: 10,
      marginLeft: 4,
    },
    centerWrap: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 32,
      paddingBottom: 48,
    },
    emptyMonkey: { width: 100, height: 100, marginBottom: 16 },
    stateTitle: {
      fontSize: theme.typography.h3.fontSize,
      fontWeight: theme.typography.h3.fontWeight,
      color: theme.black,
      marginBottom: 8,
      textAlign: 'center',
    },
    stateSubtitle: {
      fontSize: theme.typography.bodySmall.fontSize,
      color: theme.gray[500],
      textAlign: 'center',
      lineHeight: 20,
      marginBottom: 24,
    },
    retryButton: {
      paddingHorizontal: 24,
      paddingVertical: 10,
      borderRadius: 20,
      backgroundColor: theme.primary,
    },
    retryLabel: { fontSize: 14, fontWeight: '700', color: theme.onPrimary },
    skeletonCard: {
      height: 92,
      borderRadius: 20,
      backgroundColor: theme.background.cardElevated,
      marginBottom: 12,
      opacity: 0.5,
    },
  });

export const getCompetitionHeroStyles = (theme: Theme) =>
  StyleSheet.create({
    hero: {
      borderRadius: 24,
      overflow: 'hidden',
      marginTop: 4,
      marginBottom: 6,
      shadowColor: theme.primary,
      shadowOffset: { width: 0, height: 6 },
      shadowOpacity: 0.35,
      shadowRadius: 16,
      elevation: 8,
    },
    gradientFill: { ...StyleSheet.absoluteFillObject },
    heroInner: { padding: 18, paddingRight: 14 },
    // The mascot owns a right-side rail: countdown + CTA stop 100px short so it
    // stands fully visible instead of hiding behind them.
    mascot: {
      position: 'absolute',
      right: -2,
      bottom: 10,
      width: 94,
      height: 118,
    },
    heroTopRow: { flexDirection: 'row', alignItems: 'center', gap: 14 },
    /** Outer look only — CompetitionCrest owns the diameter and the fill. */
    heroLogoCircle: {
      backgroundColor: theme.timerInsetBg,
      borderWidth: 2,
      borderColor: theme.accentLight,
      shadowColor: theme.accent,
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0.5,
      shadowRadius: 10,
      elevation: 6,
    },
    heroTitleBlock: { flex: 1, minWidth: 0 },
    heroEyebrow: {
      fontSize: 11,
      fontWeight: '700',
      color: theme.accent,
      letterSpacing: 1.4,
      textTransform: 'uppercase',
      marginBottom: 3,
    },
    heroName: {
      fontSize: 22,
      fontWeight: '700',
      color: theme.white,
    },
    heroDescription: {
      fontSize: theme.typography.bodySmall.fontSize,
      // On the brand gradient only near-white reads across all three stops.
      color: theme.white,
      opacity: 0.92,
      lineHeight: 20,
      marginTop: 10,
      paddingRight: 100,
    },
    countdownStrip: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 18,
      backgroundColor: theme.timerInsetBg,
      borderRadius: 16,
      paddingVertical: 12,
      marginTop: 14,
      marginRight: 100,
    },
    countdownBlock: { alignItems: 'center', minWidth: 44 },
    countdownValue: {
      fontSize: 24,
      fontWeight: '800',
      color: theme.white,
      fontVariant: ['tabular-nums'],
    },
    countdownLabel: {
      fontSize: 10,
      color: theme.timerLabelColor,
      marginTop: 2,
      letterSpacing: 1,
      textTransform: 'uppercase',
    },
    countdownCaption: { fontSize: 12, color: theme.timerLabelColor, maxWidth: 64 },
    /** Sits in the countdown's slot, on the same inset, so the hero keeps its shape. */
    heroStatusPill: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      gap: 8,
      backgroundColor: theme.timerInsetBg,
      borderRadius: 16,
      paddingHorizontal: 14,
      paddingVertical: 9,
      marginTop: 14,
    },
    heroLiveDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: theme.success },
    heroStatusLabel: {
      fontSize: 13,
      fontWeight: '700',
      color: theme.success,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
    },
    ctaButton: {
      marginTop: 14,
      marginRight: 100,
      backgroundColor: theme.white,
      borderRadius: 22,
      paddingVertical: 13,
      alignItems: 'center',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 3 },
      shadowOpacity: 0.25,
      shadowRadius: 6,
      elevation: 4,
    },
    ctaButtonPressed: { opacity: 0.85, transform: [{ scale: 0.99 }] },
    ctaLabel: { fontSize: 15, fontWeight: '800', color: theme.primaryDark, letterSpacing: 0.3 },
  });

export const getCompetitionCardStyles = (theme: Theme) =>
  StyleSheet.create({
    card: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      backgroundColor: theme.background.card,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: theme.gray[200],
      padding: 16,
      marginBottom: 12,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 6,
      elevation: 3,
    },
    cardPressed: { opacity: 0.85 },
    cardHover: { borderColor: theme.gray[300], backgroundColor: theme.background.cardElevated },
    cardUpcoming: { borderStyle: 'dashed', borderColor: theme.gray[300] },
    cardPast: { opacity: 0.75 },
    cardCompact: { padding: 12, borderRadius: 16, gap: 10 },
    nameCompact: { fontSize: 14 },
    metaLineCompact: { fontSize: 12, marginTop: 2 },
    /** Outer look only — CompetitionCrest owns the diameter and the fill. */
    logoCircle: { backgroundColor: theme.background.inset },
    content: { flex: 1, minWidth: 0 },
    nameRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    name: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.black,
      flexShrink: 1,
    },
    liveDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: theme.success,
    },
    metaLine: {
      fontSize: theme.typography.bodySmall.fontSize,
      color: theme.gray[500],
      marginTop: 4,
    },
    joinHint: { fontSize: 13, fontWeight: '600', color: theme.accent, marginTop: 6 },
    trailing: { alignItems: 'center', gap: 6, flexDirection: 'row' },
    /** One pill shape for both lifecycle states, so the card's rhythm never shifts. */
    statusPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      backgroundColor: theme.background.inset,
      borderRadius: 12,
      paddingHorizontal: 10,
      paddingVertical: 5,
    },
    statusLiveLabel: { fontSize: 12, fontWeight: '700', color: theme.success },
  });
